import { CommuneFields } from './CommuneFields';
import { customFieldErrors, guessTerritory, type ClientFields, type ClientRecord } from '@clim/domain';
import { can } from '@clim/client-core';
import { useState, type FormEvent } from 'react';
import { fieldErrors } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';
import { ErrorBox, Field, Input, Select, Textarea } from './ui';

type FormState = Partial<ClientFields> & { territoryId?: string };

export function ClientForm({
  initial,
  territoryId,
  prefill,
  onSaved,
  onCancel,
}: {
  initial?: ClientRecord;
  /** valeurs de départ d'une création (ex. depuis un prospect) */
  prefill?: Partial<ClientFields>;
  /** territoire imposé (création depuis un dossier) */
  territoryId?: string;
  onSaved(id: string): void;
  onCancel?(): void;
}) {
  const engine = useEngine();
  const { me, api } = useReady();
  const [dupWarning, setDupWarning] = useState(false);
  const { territoryId: headerTerritory } = useTerritory();
  const territories = engine
    .list('territory')
    .filter((t) => t.isActive && (me.permissions['client.create'] === 'all' || me.territoryIds.includes(t.id)))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const [f, setF] = useState<FormState>(
    initial ?? {
      kind: 'individual',
      territoryId: territoryId ?? '',
      marketingConsent: false,
      extraPhones: [],
      customFields: {},
      ...prefill,
    },
  );
  // sous-traitance de pose : client d'un partenaire (choisi par qui planifie : administrateur, superviseur)
  const partners = engine.list('reason').filter((r) => r.category === 'partner' && (r.isActive || r.id === f.partnerId));
  const showPartner = partners.length > 0 && (can(me, 'dossier.schedule') || !!f.partnerId);
  const customDefs = me.tenant.settings.clientCustomFields.filter((d) => d.isActive !== false && (d.appliesTo === 'all' || d.appliesTo === f.kind));
  const phones = f.extraPhones ?? [];
  const setPhones = (next: typeof phones) => setF({ ...f, extraPhones: next });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  // territoire déduit (un seul autorisé, ou code postal 971 / 972…) : rien à choisir dans le cas courant
  const guessed = territoryId ?? guessTerritory(territories, f.postalCode, headerTerritory);
  const effectiveTerritory = f.territoryId || guessed || '';
  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  /** Contrôle anonyme : seulement « existe déjà », jamais qui ni chez quel commercial. */
  const checkDuplicate = async () => {
    if (!navigator.onLine) return;
    try {
      const res = await api.request<{ exists: boolean }>('POST', '/v1/clients/duplicate-check', {
        phones: [f.phone, ...(f.extraPhones ?? []).map((p) => p.number)].filter(Boolean),
        email: f.email || null,
        siret: f.siret || null,
        excludeClientId: initial?.id ?? null,
      });
      setDupWarning(res.exists);
    } catch {
      /* hors ligne ou limite atteinte : le contrôleur vérifiera */
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});
    setError(null);
    const clean = { ...f, territoryId: effectiveTerritory, extraPhones: (f.extraPhones ?? []).filter((p) => p.number.trim()) };
    const cf = customFieldErrors(me.tenant.settings.clientCustomFields, clean.kind ?? 'individual', clean.customFields ?? {});
    if (Object.keys(cf).length) {
      setErrors(cf);
      setError('Vérifiez les champs signalés');
      return;
    }
    try {
      if (initial) {
        const { territoryId: _t, ...patch } = clean;
        await engine.updateClient(initial.id, patch);
        onSaved(initial.id);
      } else {
        const id = await engine.createClient(clean as never);
        onSaved(id);
      }
    } catch (err) {
      const fe = fieldErrors(err);
      setErrors(fe);
      setError(Object.keys(fe).length ? 'Vérifiez les champs signalés' : String((err as Error).message));
    }
  };

  return (
    <form className="form" onSubmit={(e) => void submit(e)} noValidate>
      {!initial && !territoryId && territories.length > 1 && !guessed && (
        <Field label="Territoire" hint="ou saisissez le code postal : le territoire est trouvé automatiquement" error={errors.territoryId}>
          <Select value={f.territoryId ?? ''} onChange={set('territoryId')} required>
            <option value="">Choisir…</option>
            {territories.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <div className="segmented" role="radiogroup" aria-label="Type de client">
        {(['individual', 'company'] as const).map((k) => (
          <button type="button" key={k} className={f.kind === k ? 'on' : ''} onClick={() => setF({ ...f, kind: k })} aria-pressed={f.kind === k}>
            {k === 'individual' ? 'Particulier' : 'Professionnel'}
          </button>
        ))}
      </div>
      {showPartner && (
        <Field label="Client de" error={errors.partnerId} hint="client d’un partenaire : chantiers en sous-traitance de pose, sans vente">
          <Select
            value={f.partnerId ?? ''}
            disabled={!can(me, 'dossier.schedule')}
            onChange={(e) => setF({ ...f, partnerId: e.target.value || null })}
          >
            <option value="">Notre entreprise</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                Partenaire : {p.label}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {f.kind === 'company' && (
        <div className="grid2">
          <Field label="Raison sociale" error={errors.companyName}>
            <Input value={f.companyName ?? ''} onChange={set('companyName')} />
          </Field>
          <Field label="SIRET" error={errors.siret}>
            <Input value={f.siret ?? ''} onChange={set('siret')} onBlur={() => void checkDuplicate()} inputMode="numeric" />
          </Field>
          <Field label="Contact" error={errors.contactName}>
            <Input value={f.contactName ?? ''} onChange={set('contactName')} />
          </Field>
        </div>
      )}
      <div className="grid3">
        <Field label="Civilité">
          <Select value={f.civility ?? ''} onChange={set('civility')}>
            <option value="">—</option>
            <option value="MME">Mme</option>
            <option value="M">M.</option>
            <option value="AUTRE">Autre</option>
          </Select>
        </Field>
        <Field label="Nom" error={errors.lastName}>
          <Input value={f.lastName ?? ''} onChange={set('lastName')} autoComplete="family-name" />
        </Field>
        <Field label="Prénom" error={errors.firstName}>
          <Input value={f.firstName ?? ''} onChange={set('firstName')} autoComplete="given-name" />
        </Field>
      </div>
      <div className="grid2">
        <Field label="Téléphone" error={errors.phone}>
          <Input value={f.phone ?? ''} onChange={set('phone')} onBlur={() => void checkDuplicate()} type="tel" autoComplete="tel" />
        </Field>
        <Field label="E-mail" error={errors.email}>
          <Input value={f.email ?? ''} onChange={set('email')} onBlur={() => void checkDuplicate()} type="email" autoComplete="email" />
        </Field>
      </div>
      {phones.map((p, i) => (
        <div className="grid2" key={i}>
          <Field label="Libellé">
            <Input value={p.label ?? ''} onChange={(e) => setPhones(phones.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Bureau, conjoint…" />
          </Field>
          <Field label={`Numéro ${i + 2}`} error={errors[`extraPhones.${i}.number`]}>
            <div className="inline">
              <Input value={p.number} type="tel" onChange={(e) => setPhones(phones.map((x, j) => (j === i ? { ...x, number: e.target.value } : x)))} />
              <button type="button" className="btn small" onClick={() => setPhones(phones.filter((_, j) => j !== i))} aria-label="Retirer ce numéro">
                ✕
              </button>
            </div>
          </Field>
        </div>
      ))}
      {phones.length < 5 && (
        <button type="button" className="link" onClick={() => setPhones([...phones, { label: null, number: '' }])}>
          + Ajouter un numéro
        </button>
      )}
      <Field label="Adresse de facturation" error={errors.addressLine1}>
        <Input value={f.addressLine1 ?? ''} onChange={set('addressLine1')} autoComplete="address-line1" />
      </Field>
      <div className="grid2">
        <CommuneFields
          postalCode={f.postalCode}
          city={f.city}
          errors={{ postalCode: errors.postalCode, city: errors.city }}
          onChange={(v) => setF({ ...f, ...v })}
        />
      </div>
      {customDefs.map((d) => {
        const v = f.customFields?.[d.code];
        const setV = (x: string | number | boolean | null) => setF({ ...f, customFields: { ...(f.customFields ?? {}), [d.code]: x } });
        return (
          <Field key={d.code} label={d.required ? `${d.label} *` : d.label} error={errors[`customFields.${d.code}`]}>
            {d.type === 'boolean' ? (
              <input type="checkbox" checked={v === true} onChange={(e) => setV(e.target.checked)} />
            ) : d.type === 'select' ? (
              <Select value={(v as string) ?? ''} onChange={(e) => setV(e.target.value || null)}>
                <option value="">—</option>
                {(d.options ?? []).map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </Select>
            ) : (
              <Input
                type={d.type === 'number' ? 'number' : 'text'}
                value={(v as string | number | null) ?? ''}
                onChange={(e) => setV(e.target.value === '' ? null : d.type === 'number' ? Number(e.target.value) : e.target.value)}
              />
            )}
          </Field>
        );
      })}
      <Field label="Notes">
        <Textarea value={f.notes ?? ''} onChange={set('notes')} />
      </Field>
      <label className="check">
        <input type="checkbox" checked={!!f.marketingConsent} onChange={(e) => setF({ ...f, marketingConsent: e.target.checked })} />
        Le client accepte de recevoir des offres commerciales
      </label>
      {dupWarning && (
        <p className="warning-box" role="status">
          Un client avec ces coordonnées existe déjà dans l’entreprise. Vous pouvez continuer : le contrôleur vérifiera
          le doublon lors du contrôle du dossier.
        </p>
      )}
      <ErrorBox message={error} />
      <div className="actions">
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>
            Annuler
          </button>
        )}
        <button type="submit" className="btn primary">
          Enregistrer
        </button>
      </div>
    </form>
  );
}
